import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "senf-shared";
import type { Project } from "senf-shared/types";
import { useFileCache } from "../../../hooks/useFileCache";
import FileDrop from "../../core/fileDrop/FileDrop";
import Toast from "../../core/toast/Toast";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import Tabs from "../../molecules/tabs/Tabs";
import type { TabsProps } from "../../molecules/tabs/Tabs.types";
import AiImage from "../survey/components/AiImage";
import UnsplashGallery from "../survey/components/UnsplashGallery";
import { ActionBar, deleteStorageFile } from "./ActionBar";
import MediaBrowser from "./MediaBrowser";

export type ImageInput = {
  url: string;
  alt: string;
  name: string;
  size: number;
  type: string;
};
export interface ImagesUploadModalProps {
  onInputChange?: (files: ImageInput[]) => Promise<void>;
  fileTypes?: string[];
  stock?: boolean;
  ai?: boolean;
  basePath: string;
  saveToPath?: string;
  mediaBrowser?: boolean;
  fileTypeLabel?: {
    singular: string;
    plural: string;
  };
  currentImageUrl?: string;
  maxItems?: number;
  project?: Project;
}

const ImagesUploadModal = ({
  onInputChange,
  fileTypes = ["image/jpg", "image/jpeg", "image/png", "image/webp"],
  stock,
  ai,
  basePath,
  saveToPath,
  mediaBrowser,
  fileTypeLabel,
  currentImageUrl,
  maxItems = 1,
  project,
}: ImagesUploadModalProps) => {
  const { t } = useTranslation();
  const { closeModal } = useModals();
  const { user } = useAuthContext();
  const fileCache = useFileCache();
  const closedDeliberatly = useRef(false);

  useEffect(() => {
    closedDeliberatly.current = true;
    return () => {
      if (closedDeliberatly.current) {
        setGeneratedImage((current) => {
          if (current) deleteStorageFile(current.id);
          return null;
        });
      }
    };
  }, []);

  const [tabIndex, setTabIndex] = useState<string>("mediaBrowser");
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{
    stock: ImageInput[];
    ai: ImageInput[];
    mediaBrowser: ImageInput[];
  }>({
    stock: [],
    ai: [],
    mediaBrowser: [],
  });
  const [generatedImage, setGeneratedImage] = useState<{
    id: string;
    url: string;
    name: string;
    path: string;
    size: number;
    width?: number;
    height?: number;
  } | null>(null);
  const [errors, setErrors] = useState<{
    stock: string;
    ai: string;
    mediaBrowser: string;
  }>({
    stock: "",
    ai: "",
    mediaBrowser: "",
  });

  // Persist and restore active tab per currentImageUrl in sessionStorage,
  // and fall back to a global most-recent tab when no currentImageUrl is provided
  const perUrlKey = useMemo(() => {
    if (!currentImageUrl) return null;
    try {
      const url = new URL(currentImageUrl);
      return `imagesUploadModal:${url.origin}${url.pathname}`;
    } catch {
      // Fallback: strip query/hash manually
      const base =
        currentImageUrl.split("?")[0]?.split("#")[0] || currentImageUrl;
      return `imagesUploadModal:${base}`;
    }
  }, [currentImageUrl]);
  const lastTabKey = "imagesUploadModal:__last__";

  const availableTabs = useMemo(() => {
    const arr = ["mediaBrowser"] as string[];
    if (stock) arr.push("stock");
    if (ai) arr.push("ai");
    return arr;
  }, [stock, ai]);

  useEffect(() => {
    try {
      const keyToRead = perUrlKey || lastTabKey;
      const saved = JSON.parse(
        window.sessionStorage.getItem(keyToRead) || "{}",
      );
      const savedTab = saved?.tab as string | undefined;
      if (savedTab && availableTabs.includes(savedTab)) {
        setTabIndex(savedTab);
      }
    } catch {
      // noop
    }
  }, [perUrlKey, availableTabs]);

  useEffect(() => {
    try {
      // Always persist the most-recent tab globally
      const savedLast = JSON.parse(
        window.sessionStorage.getItem(lastTabKey) || "{}",
      );
      window.sessionStorage.setItem(
        lastTabKey,
        JSON.stringify({ ...savedLast, tab: tabIndex }),
      );

      // Additionally persist tab for this specific currentImageUrl when available
      if (perUrlKey) {
        const savedPerUrl = JSON.parse(
          window.sessionStorage.getItem(perUrlKey) || "{}",
        );
        window.sessionStorage.setItem(
          perUrlKey,
          JSON.stringify({ ...savedPerUrl, tab: tabIndex }),
        );
      }
    } catch {
      // noop
    }
  }, [perUrlKey, tabIndex]);
  const handleSetSelectedFiles = (
    tabIndex: string,
    setState: React.Dispatch<React.SetStateAction<ImageInput[]>>,
  ) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [tabIndex]:
        typeof setState === "function" ? setState(prev[tabIndex]) : setState,
    }));
  };

  const handleFiles = async (files: FileList | File[]) => {
    setIsUploading(true);
    await onInputChange?.([...files]);
    setIsUploading(false);
    closedDeliberatly.current = false;
    closeModal();
  };

  const tabs: TabsProps["tabs"] = [
    {
      tab: "mediaBrowser",
      text: mediaBrowser ? t("global:mediaLibrary") : "Upload",
    },
  ];

  if (stock) {
    tabs.push({
      tab: "stock",
      text: "Stock Photos",
    });
  }
  if (ai) {
    tabs.push({
      tab: "ai",
      text: "AI Generator",
    });
  }

  const handleSubmit = (selectedImages: ImageInput[]) => {
    closedDeliberatly.current = false;
    onInputChange?.(selectedImages);
    closeModal();
  };

  return (
    <div className="h-full w-full grid grid-rows-[auto_1fr]">
      <div className="sticky top-0 bg-surface-default border-b border-border-subdued z-10">
        <Tabs
          active={tabIndex}
          classNames={{ wrapper: "mx-5 h-12" }}
          setActive={(e) => setTabIndex(e as string)}
          tabs={tabs}
        />
      </div>

      <div className="bg-surface-subdued overflow-hidden">
        {tabIndex === "mediaBrowser" ? (
          mediaBrowser ? (
            <MediaBrowser
              basePath={basePath}
              saveToPath={saveToPath}
              currentImageUrl={currentImageUrl}
              fileTypeLabel={fileTypeLabel}
              maxItems={maxItems}
              fileTypes={fileTypes}
              errors={errors[tabIndex]}
              selectedFiles={selectedFiles[tabIndex]}
              setSelectedFiles={(e) => handleSetSelectedFiles(tabIndex, e)}
              fileCacheHook={fileCache}
              project={project}
            />
          ) : (
            <FileDrop
              onDrop={handleFiles}
              showImage={false}
              multiple={maxItems !== 1}
              fileTypes={fileTypes}
              loading={isUploading}
            />
          )
        ) : tabIndex === "stock" ? (
          <UnsplashGallery
            maxImages={maxItems}
            selectedImages={selectedFiles[tabIndex]}
            setSelectedImages={(e) => handleSetSelectedFiles(tabIndex, e)}
            currentImageUrl={currentImageUrl}
          />
        ) : (
          <AiImage
            basePath={basePath}
            saveToPath={saveToPath}
            currentImageUrl={currentImageUrl}
            selectedImages={selectedFiles[tabIndex]}
            setSelectedImages={(e) => handleSetSelectedFiles(tabIndex, e)}
            generatedImage={generatedImage}
            setGeneratedImage={setGeneratedImage}
            project={project}
          />
        )}
      </div>

      <ActionBar
        basePath={basePath}
        selectedFiles={selectedFiles[tabIndex]}
        setSelectedFiles={(e) => handleSetSelectedFiles(tabIndex, e)}
        fileTypeLabel={fileTypeLabel}
        fileTypes={fileTypes}
        userId={user?.uid}
        handleError={setErrors}
        onSubmit={handleSubmit}
        currentImageUrl={currentImageUrl}
        tab={tabIndex}
        setGeneratedImage={setGeneratedImage}
        fileCacheHook={fileCache}
      />

      <Toast
        id="mediaFilesError"
        leadingIcon="alert-fill"
        leadingIconClassName="text-icon-critical"
        show={errors[tabIndex]}
      >
        {errors[tabIndex]}
      </Toast>
    </div>
  );
};

export default ImagesUploadModal;
